import $ from 'jquery';
import '@mux/mux-player';

import { v1BaseUrl } from './config.js';
import { watchNavBarHtml, showLoader, hideLoader } from '/js/navigation.js';
import { navigate } from '/js/router.js';
import { userData } from '/js/accounts.js';
import { mkToast } from '@shared/js/toasts.js';
import { isPlayableUrl, preferredPlaybackMode, reportInvalidPlaybackSource, watchPlayerErrors } from '@shared/js/muxPlayer.js';

// Player lifecycle state. Only one player exists at a time — teardownPlayer()
// flushes progress and detaches window-level listeners whenever the user
// leaves the watch view (invoked by the router) or a new player renders.
let currentUpdateInterval = null;
let currentPlayer = null;
let currentShowId = null;
let detachWindowListeners = null;
let detachPlayerErrors = null;

// How often progress is persisted while playback is running.
const PROGRESS_SAVE_INTERVAL_MS = 15000;
// Minimum spacing between saves triggered by scrubbing (seeked can fire in
// rapid bursts while the user drags the timeline).
const SEEK_SAVE_THROTTLE_MS = 2000;

// Persist the current playback position with a beacon. Beacons survive tab
// close / page navigation because the browser completes them in the
// background — a regular XHR would be cancelled mid-flight.
function beaconProgress() {
  if (!currentPlayer || !currentShowId) return;
  const position = currentPlayer.currentTime;
  if (!position || position <= 0) return;
  if (navigator.sendBeacon) {
    // URLSearchParams posts as application/x-www-form-urlencoded, which the
    // progress endpoint already reads and which needs no CORS preflight.
    navigator.sendBeacon(
      v1BaseUrl + 'show/progress',
      new URLSearchParams({ show: currentShowId, position: String(position) })
    );
  }
}

// Flush progress and tear down all player timers/listeners. Exported so the
// router can call it whenever navigation leaves the watch view.
export function teardownPlayer() {
  if (currentUpdateInterval) {
    clearInterval(currentUpdateInterval);
    currentUpdateInterval = null;
  }
  // Final save so leaving mid-playback doesn't lose up to 15s of progress.
  beaconProgress();
  if (detachWindowListeners) {
    detachWindowListeners();
    detachWindowListeners = null;
  }
  if (detachPlayerErrors) {
    detachPlayerErrors();
    detachPlayerErrors = null;
  }
  currentPlayer = null;
  currentShowId = null;
}

export async function renderPlayer(showId, { fromBeginning = false } = {}) {
  // Flush any previous player instance before its DOM is removed.
  teardownPlayer();

  const app = $("#app");
  app.empty();

  showLoader();

  try {

	// Data
	let show = await fetchShow(showId);
	let showTitle = show.title;
        let signedUrl = await authorizeStream(showId);
        // Auto-resume from saved progress (no prompt) — the details CTA
        // already showed a Resume button with a progress line. The "Play from
        // Beginning" button arrives with fromBeginning set, which skips the
        // seek; saved progress is then overwritten as playback runs.
        let progress = fromBeginning ? null : await fetchProgress(showId);

        // Back from the player returns to the show's details page so users
        // can re-read the description or browse extras.
        const nav = watchNavBarHtml(showTitle, '/shows/' + encodeURIComponent(showId)).addClass("player-nav");

        const iframeWrap = $('<div class="container-fluid h-100 p-0 bg-black player-iframe-wrap"></div>').addClass("player-iframe-wrap");

        // Attributes are set via .attr() so titles with quotes can't break
        // the markup.
        const playerEl = $('<mux-player id="streamPlayer" style="background-color: black; border: none; height: 100%; width: 100%;" stream-type="on-demand" accent-color="#922727"></mux-player>')
                .attr('src', signedUrl)
                .attr('metadata-video-title', showTitle)
                .attr('metadata-viewer-user-id', userData.guid);

        // Only set on browsers whose native HLS support can't be trusted;
        // elsewhere mux-player's own choice is left in place.
        const preferPlayback = preferredPlaybackMode();
        if (preferPlayback) playerEl.attr('prefer-playback', preferPlayback);

        // The player handles its own failures — it paints an error dialog and
        // dispatches an event, but never throws — so playback errors are
        // invisible to Sentry unless they're forwarded by hand.
        detachPlayerErrors = watchPlayerErrors(playerEl[0], {
                source: 'watch',
                showId,
                showTitle,
        });

        // Save progress when the tab is backgrounded or the page unloads —
        // the moments a regular AJAX save is most likely to be lost.
        const onPageHide = () => beaconProgress();
        const onVisibilityChange = () => {
                if (document.hidden) beaconProgress();
        };
        window.addEventListener('pagehide', onPageHide);
        document.addEventListener('visibilitychange', onVisibilityChange);
        detachWindowListeners = () => {
                window.removeEventListener('pagehide', onPageHide);
                document.removeEventListener('visibilitychange', onVisibilityChange);
        };

        playerEl.on('loadeddata', async function() {
                const player = document.getElementById('streamPlayer');
                currentPlayer = player;
                currentShowId = showId;

                if (progress && progress.position > 0) {
                        player.currentTime = progress.position;
                }

                player.play();

                // Periodic save while playing; event-driven saves on pause,
                // seek and end so the resume position tracks what the user
                // actually did rather than the last 15s tick.
                player.addEventListener('play', () => {
                        clearInterval(currentUpdateInterval);
                        currentUpdateInterval = setInterval(() => {
                                if (!player.paused && !player.ended) {
                                        updateProgress(player, showId);
                                }
                        }, PROGRESS_SAVE_INTERVAL_MS);
                });

                player.addEventListener('pause', () => {
                        clearInterval(currentUpdateInterval);
                        updateProgress(player, showId);
                });

                player.addEventListener('ended', () => {
                        clearInterval(currentUpdateInterval);
                        updateProgress(player, showId);
                });

                let lastSeekSave = 0;
                player.addEventListener('seeked', () => {
                        const now = Date.now();
                        if (now - lastSeekSave < SEEK_SAVE_THROTTLE_MS) return;
                        lastSeekSave = now;
                        updateProgress(player, showId);
                });
        });


	const playerContainer = $('<div class="player-container"></div>');
	playerContainer.append(nav, iframeWrap);

	// Append the elements to the DOM
        iframeWrap.append(playerEl);
	app.append(playerContainer);
	hideLoader();

	// Check for iOS device and PWA
	function isIOS() {
	  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
	}

	function isPWAInstalled() {
	  return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
	}

	if (isIOS() && isPWAInstalled()) {
	  iframeWrap.addClass('pb-5');
	}
  } catch (error) {
	console.error("Error rendering player:", error);
	hideLoader();
	// Tell the user why playback didn't start, then send them back to the
	// show's details page rather than dumping them on the vault — they may
	// want to retry from the same context (and the URL stays meaningful).
	mkToast('player', 'danger', 'Playback Error', error.message || 'Unable to start playback. Please try again.', 8000);
	navigate('/shows/' + encodeURIComponent(showId), { replace: true });
  }
}

function fetchProgress(showId) {
  return new Promise((resolve, reject) => {
        $.ajax({
          url: `${v1BaseUrl}show/progress`,
          type: "POST",
          svErrorMode: 'default',
          data: { show: showId },
          xhrFields: {
                withCredentials: true
          },
	  success: function(data) {
		resolve(data);
	  },
	  error: function(jqXHR, textStatus, errorThrown) {
		console.error("Error fetching progress:", textStatus, errorThrown);
		reject(new Error("Unable to fetch progress"));
	  }
	});
  });
}

function fetchShow(id) {
  return new Promise((resolve, reject) => {
        $.ajax({
          url: v1BaseUrl + "show/info",
          type: "POST",
          svErrorMode: 'default',
          xhrFields: {
                withCredentials: true
          },
	  data: {
	  	show: id
	  },
	  dataType: "json",
	  success: function (data) {
		if (data) {
		  resolve(data);
		} else {
		  reject(new Error("No show found in the response"));
		}
	  },
	  error: function (jqXHR, textStatus, errorThrown) {
		// Handle errors here
		console.error("Error fetching show: " + textStatus, errorThrown);
		reject(new Error("Unable to fetch show"));
	  },
	});
  });
}

function authorizeStream(id) {
        return new Promise((resolve, reject) => {
                $.ajax({
                  url: v1BaseUrl + "show/stream",
                  type: "POST",
                  svErrorMode: 'default',
                  xhrFields: {
                        withCredentials: true
                  },
                  data: {
                                show: id
                        },
                  dataType: "json",
                  success: function (data) {
                        if (data.authorized && isPlayableUrl(data.url)) {
                                resolve(data.url);
                        } else if (data.authorized) {
                                // Entitled, but the API couldn't produce a real
                                // playback URL. Handing the player a non-URL
                                // string makes it fetch that path off our own
                                // host, demux the SPA's index.html and show an
                                // opaque "Source Not Supported" dialog — stop
                                // here instead, and report why.
                                reportInvalidPlaybackSource(data.url, { endpoint: 'show/stream', showId: id });
                                reject(new Error("This video is temporarily unavailable. Please try again later."));
                        } else {
                                reject(new Error("403 · You are not authorized to view this content."));
                        }
                  },
                  error: function (jqXHR, textStatus, errorThrown) {
                        console.error("Error authorizing stream: " + textStatus, errorThrown);
                        reject(new Error("An unknown error occurred while authorizing playback. Please try again later."));
                  },
                });
          });
}


function updateProgress(player, showId) {
	const position = player.currentTime;
	if (!position || position <= 0) return;

        $.ajax({
                url: `${v1BaseUrl}show/progress`,
                type: "POST",
                svErrorMode: 'default',
                data: {
                        show: showId,
                        position: position
                },
		xhrFields: {
			withCredentials: true
		},
                success: function() {
                        // Progress updated successfully
                },
		error: function(jqXHR, textStatus, errorThrown) {
			console.error("Error updating progress:", textStatus, errorThrown);
		}
	});
}
